import type { QueryClient } from '@tanstack/react-query';
import type { ItemFieldValue } from '@/lib/api/item-store/field-values';
import type { Item, ItemMultiLinkValue } from '@/lib/api/types/items';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { loopVisualizationQueryKeys } from '@/lib/query-keys/loop-visualizations';

export function reconcileMultiValues(
  fieldType: string,
  fieldValue: ItemFieldValue['fieldValue'],
): unknown[] {
  if (fieldType === 'MULTI_LINK') {
    return fieldValue.map(({ id, value, description }): ItemMultiLinkValue => {
      const entry: ItemMultiLinkValue = { id, MULTI_LINK: value as string };
      if (description !== undefined) {
        entry.description = description;
      }
      return entry;
    });
  }
  return fieldValue.map(({ id, value }) => ({ id, [fieldType]: value }));
}

const ITEMS_BULK_FETCH_KEY = ['itemsBulkFetch'] as const;
const ITEMS_BULK_FETCH_V2_KEY = ['itemsBulkFetchV2'] as const;

export function patchItemCache(
  queryClient: QueryClient,
  itemId: number,
  patch: Partial<Item>,
): Item | undefined {
  const snapshot = queryClient.getQueryData<Item>(commonQueryKeys.item(itemId));
  if (snapshot) {
    queryClient.setQueryData<Item>(commonQueryKeys.item(itemId), (old) =>
      old ? ({ ...old, ...patch } as Item) : old,
    );
  }
  return snapshot;
}

export function restoreItemCache(
  queryClient: QueryClient,
  itemId: number,
  snapshot: Item | undefined,
): void {
  if (snapshot !== undefined) {
    queryClient.setQueryData(commonQueryKeys.item(itemId), snapshot);
  }
}

export function invalidateItemCaches(queryClient: QueryClient, itemId: number): void {
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.item(itemId) });
  void queryClient.invalidateQueries({
    queryKey: loopVisualizationQueryKeys.loopItem.detail(itemId),
  });
  void queryClient.invalidateQueries({ queryKey: ITEMS_BULK_FETCH_V2_KEY });
  void queryClient.invalidateQueries({ queryKey: ITEMS_BULK_FETCH_KEY });
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsTreeSet.all() });
}
